// Third party components
import React, { Children, cloneElement, useState, forwardRef, useImperativeHandle } from 'react';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import {Box, Tabs, Tab} from '@mui/material';

// Shared components
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useToggle } from '@timmons-group/shared-react-components/hooks';

const MapSidebar = forwardRef(({ firstId, children, isOpen, className, ...props }, ref) => {
  const kids = Children.toArray(children);
  const [selected, setSelected] = useState(firstId || kids[0].props.id);
  const {handleToggle, forceOpen, toggleComplete, toggleCss} = useToggle(isOpen);

  // Expose some methods to the parent component
  useImperativeHandle(ref, () => {
    return {
      forceOpen,
      toggle: handleToggle,
      select: (id) => setSelected(id),
    };
  });

  return (
    <Box className={`${className || 'sidebar'} ${toggleCss}`} {...props} onAnimationEnd={toggleComplete}>
      <Box className="collapsed-content" onClick={handleToggle}>
        <div className="collapse-icon">
          <ExpandMoreIcon color="secondary"/>
        </div>
      </Box>
      <Box className="sidebar-panel-content" sx={{ padding: '8px' }}>
        <Box className="sidebar-header">
          <h5 className="header-label">Tools</h5>
          <div className="collapse-icon" onClick={handleToggle}>
            <ExpandMoreIcon color="secondary" />
          </div>
        </Box>
        <Tabs
          sx={{marginTop: 1}}
          variant="fullWidth"
          value={selected}
          onChange={(event, newValue) => setSelected(newValue)}
          aria-label="map sidebar tabs"
        >
          {kids.map(child => {
            const childId = child.props.id;
            return (
              <StyledTab
                key={childId}
                value={childId}
                label={child.props.label || childId}
                id={`map-tab-${childId}`}
                aria-controls={`map-panel-${childId}`}
              />)
          })}
        </Tabs>
        {
          kids.map(child => {
            const isSelected = child.props.id === selected;
            if (!isSelected) {
              return null;
            }

            return (
              <Box key={child.props.id} role="tabpanel" id={`map-panel-${child.props.id}`} aria-labelledby={`map-panel-${child.props.id}`}>
                {cloneElement(child, {
                  ...child.props,
                })}
              </Box>
            );
          })
        }
      </Box >
    </Box >
  );
}
);
MapSidebar.displayName = 'MapSidebar';

MapSidebar.propTypes = {
  children: PropTypes.node.isRequired,
  firstId: PropTypes.string,
  isOpen: PropTypes.bool,
  className: PropTypes.string,
}

export default MapSidebar;

const StyledTab = styled(Tab)(({ theme }) => {
  const {mapSidebar} = theme.palette;
  return {
    background: mapSidebar.background,
    color: mapSidebar.text,
    textTransform: 'none',
    fontSize: '12px',
    fontWeight: 'bold',
    '&.Mui-selected': {
      backgroundColor: mapSidebar.selectedBackground,
      color: mapSidebar.selectedText,
    },
    '&.Mui-selected:hover': {
      backgroundColor: mapSidebar.selectedBackground,
      color: mapSidebar.selectedText,
    },
    '&.Mui-selected:focus': {
      backgroundColor: mapSidebar.selectedBackground,
      color: mapSidebar.selectedText,
    }
  }
});


/**
 * helper componement to create a sidebar
 * id and string will be used in the tabs render
 * id - string
 * label - string
 * children - React.Node
 * @param {*} param0
 * @returns
 */
export const SidebarItem = ({ children }) => {
  return (
    <Box component="div" className="sidebar-item">
      {children}
    </Box>
  );
}

SidebarItem.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  children: PropTypes.node,
}